import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getErrorMessage } from '@/utils/errorHandler';
import { FullScreenSignupLayout } from '@/components/ui/full-screen-signup';
import { apiService } from '@/services/api';
import { FormInput, FormLabel } from '@/components/ui/form-primitives';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/useToast';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiService.client.post('/api/auth/password-reset/request', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (error: unknown) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FullScreenSignupLayout
      leftHeadline=""
      rightTitle="Forgot password?"
      rightSubtitle={sent ? "Check your email" : "Enter your email to receive a reset link"}
    >
      {sent ? (
        <div className="flex flex-col gap-5">
          <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 flex gap-3 text-sm">
            <span className="material-symbols-outlined text-lg shrink-0 text-emerald-600">mark_email_read</span>
            <p className="font-medium pt-0.5 text-emerald-900">
              We've sent a password reset link to <strong>{email}</strong>. Check your inbox.
            </p>
          </div>
          <Link
            to="/login"
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-primary hover:bg-[#2d3166] text-white rounded-lg font-medium shadow-md shadow-[#393e7f]/20 transition-colors"
          >
            Back to login
          </Link>
        </div>
      ) : (
        <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <FormLabel htmlFor="forgot-email">
              Email
            </FormLabel>
            <FormInput
              id="forgot-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <Button
            type="submit"
            loading={loading}
            disabled={!email}
            className="w-full min-h-12"
            size="xl"
          >
            Send reset link
          </Button>
        </form>
      )}

      <div className="mt-4 text-center text-sm text-slate-600">
        Remember your password?{' '}
        <Link to="/login" className="font-semibold text-[#393e7f] hover:text-[#2d3166] underline underline-offset-2">
          Log in
        </Link>
      </div>
    </FullScreenSignupLayout>
  );
};

export default ForgotPasswordPage;
